
import { useState } from 'react';
import Button from '../../../components/base/Button';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    useCase: '',
    timeline: '',
    ndaRequired: false,
    pilotInquiry: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const response = await fetch('https://readdy.ai/api/form/d2t8enisae4nj889jns0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          useCase: '',
          timeline: '',
          ndaRequired: false,
          pilotInquiry: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-20 bg-gradient-to-br from-blue-600 to-blue-800">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Transform Your Operations?</h2>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto">
            Get in touch with our robotics experts to discuss your specific needs and explore 
            how BC Robotics can enhance safety and productivity at your facility.
          </p>
        </div>

        <div className="bg-white rounded-3xl shadow-2xl p-8 lg:p-12">
          <form id="bc-robotics-contact" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  required
                  value={formData.name}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                  placeholder="Enter your full name"
                />
              </div>
              
              <div>
                <label htmlFor="company" className="block text-sm font-semibold text-gray-900 mb-2">
                  Company *
                </label>
                <input
                  type="text"
                  id="company"
                  name="company"
                  required
                  value={formData.company}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                  placeholder="Your company name"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                  placeholder="your.email@company.com"
                />
              </div>
              
              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm"
                  placeholder="+82 10-0000-0000"
                />
              </div>
            </div>

            <div>
              <label htmlFor="useCase" className="block text-sm font-semibold text-gray-900 mb-2">
                Use Case / Application *
              </label>
              <textarea
                id="useCase"
                name="useCase"
                required
                rows={4}
                maxLength={500}
                value={formData.useCase}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm resize-none"
                placeholder="Describe your specific robotics needs, current challenges, and operational requirements..."
              />
              <div className="text-right text-xs text-gray-500 mt-1">
                {formData.useCase.length}/500 characters
              </div>
            </div>

            <div>
              <label htmlFor="timeline" className="block text-sm font-semibold text-gray-900 mb-2">
                Implementation Timeline
              </label>
              <select
                id="timeline"
                name="timeline"
                value={formData.timeline}
                onChange={handleInputChange}
                className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select timeline</option>
                <option value="immediate">Immediate (within 3 months)</option>
                <option value="short-term">Short-term (3-6 months)</option>
                <option value="medium-term">Medium-term (6-12 months)</option>
                <option value="long-term">Long-term (12+ months)</option>
                <option value="exploratory">Exploratory / Research phase</option>
              </select>
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="ndaRequired"
                  name="ndaRequired"
                  checked={formData.ndaRequired}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600 cursor-pointer"
                />
                <label htmlFor="ndaRequired" className="ml-3 text-sm text-gray-700 cursor-pointer">
                  NDA required for detailed discussions
                </label>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="pilotInquiry"
                  name="pilotInquiry"
                  checked={formData.pilotInquiry}
                  onChange={handleInputChange}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-600 cursor-pointer"
                />
                <label htmlFor="pilotInquiry" className="ml-3 text-sm text-gray-700 cursor-pointer">
                  Interested in pilot program opportunity
                </label>
              </div>
            </div>

            {submitStatus === 'success' && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-check-circle-line text-green-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-green-800 font-medium">
                    Thank you! We'll contact you within 24 hours to discuss your robotics needs.
                  </span>
                </div>
              </div>
            )}

            {submitStatus === 'error' && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <i className="ri-error-warning-line text-red-600 mr-2 w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-red-800 font-medium">
                    Something went wrong. Please try again or contact us directly.
                  </span>
                </div>
              </div>
            )}

            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting || formData.useCase.length > 500}
              className="w-full"
            >
              {isSubmitting ? (
                <>
                  <i className="ri-loader-4-line mr-2 animate-spin"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line mr-2"></i>
                  Request Demo & Consultation
                </>
              )}
            </Button>
          </form>
        </div>
      </div>
    </section>
  );
}
